import { useCallback, useMemo, useRef, useState } from 'react';
import { fetchInteractionsAll } from '../../../../../../../services/interactionsHistory.service';
import { generateAllColumns } from '../shared/columns';

export type AllFilters = {
    historyType?: 'APP' | 'CHAT' | 'CHATBOT' | 'TELEFONÍA' | 'PQR';
    channelId?: string;
    identification?: string;
    plate?: string;
    policy?: string;
};

function normalizeFilters(f: AllFilters) {
    return {
        historyType: f.historyType,
        channelId:
            f.historyType === 'PQR' ? f.channelId || undefined : undefined,
        identification: f.identification?.trim() || undefined,
        plate: f.plate?.trim() || undefined,
        policy: f.policy?.trim() || undefined,
    };
}

export function useHistoryAllData(
    initial: AllFilters,
    pageSize = 20,
    autoload = true
) {
    const [filters, setFilters] = useState<AllFilters>(initial ?? {});
    const lastSubmittedRef = useRef<AllFilters | null>(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize,
    });
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState(0);
    const [enabled, setEnabled] = useState<boolean>(autoload);
    const columns = useMemo(generateAllColumns, []);

    const offset = paginationModel.page * paginationModel.pageSize;
    const limit = paginationModel.pageSize;

    const loadWith = useCallback(
        async (f: AllFilters) => {
            setLoading(true);
            try {
                const params = normalizeFilters(f);
                const res = await fetchInteractionsAll({
                    ...params,
                    offset,
                    limit,
                });
                const payload = res?.items;
                const data = payload?.data ?? [];
                const total = payload?.total ?? data.length;
                setRows(data);
                setRowCount(total);
            } catch (error) {
                console.error(error);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        },
        [offset, limit]
    );

    const enable = () => setEnabled(true);

    const submitFilters = (f: AllFilters) => {
        setPaginationModel((p) => ({ ...p, page: 0 }));
        lastSubmittedRef.current = f;
        setFilters(f);
    };

    const resetFilters = () => {
        const base: AllFilters = { identification: initial.identification };
        setPaginationModel((p) => ({ ...p, page: 0 }));
        lastSubmittedRef.current = base;
        setFilters(base);
    };

    const search = async () => {
        if (!enabled) setEnabled(true);
        const f = lastSubmittedRef.current ?? filters;
        await loadWith(f);
    };

    return {
        rows,
        rowCount,
        loading,
        columns,
        paginationModel,
        setPaginationModel,
        filters,
        submitFilters,
        resetFilters,
        search,
        enable,
    };
}
